import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Group, Select, Switch, Table, Tabs, Text, TextInput, Title } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Container, LayoutStandard, Loader, Modal } from 'components';
import { IRootState } from 'config/redux';
import { AuthorityConstants, RouteConstants, ServiceConstants } from 'constant';
import { ProfileService, UserService } from 'services';
import { displayClasses, tableClasses } from 'styles';
import { AuthorityResponse, ProfileExtendedResponse, UserResponse } from 'types';
import { AppUtils } from 'utils';

const { 
  REQUEST_PENDING,
  REQUEST_PROFILES_LIST_ID,
  REQUEST_PROFILES_PERMISSIONS_ID,
  REQUEST_USERS_LIST_ID,
  REQUEST_USERS_UPDATE_ID,
  REQUEST_PROFILE_AGENT_CREATE_ID,
} = ServiceConstants;

const AgentPermissionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Redux selectors
  const profilesServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_LIST_ID]);
  const permissionsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_PERMISSIONS_ID]);
  const usersServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID]);
  const updateServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_UPDATE_ID]);
  const profileAgentCreateServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILE_AGENT_CREATE_ID]);

  // Local state
  const [profiles, setProfiles] = useState<ProfileExtendedResponse[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<AuthorityResponse[]>([]);
  const [agent, setAgent] = useState<UserResponse | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load data on mount
  useEffect(() => {
    ProfileService.profilesAll({ page: 0, search: '', pageSize: 100 });
    ProfileService.profilePermissionsAll();
    UserService.usersAll({ page: 0, search: '', pageSize: 100, type: 'EMPLOYEE' });
  }, []);

  // Handle profiles loaded
  useEffect(() => {
    AppUtils.resolveServiceObject(profilesServiceObject, REQUEST_PROFILES_LIST_ID, (data) => {
      setProfiles(data.content || data);
    });
  }, [profilesServiceObject]);

  // Handle permissions loaded
  useEffect(() => {
    AppUtils.resolveServiceObject(permissionsServiceObject, REQUEST_PROFILES_PERMISSIONS_ID, (data) => {
      if (data && data.length > 0) {
        setAvailablePermissions(data);
      }
    });
  }, [permissionsServiceObject]);

  // Handle users loaded - find the specific user
  useEffect(() => {
    AppUtils.resolveServiceObject(usersServiceObject, REQUEST_USERS_LIST_ID, (data) => {
      const users = data.content || data;
      const foundUser = users.find((u: UserResponse) => u.id === parseInt(id || '0'));
      if (foundUser) {
        setAgent(foundUser);
        setSelectedTemplate(foundUser.profile?.id?.toString() || null);
        const authCodes = (foundUser.profile?.authorities || []).map((auth: any) => 
          typeof auth === 'string' ? auth : auth.code
        );
        setSelectedAuthorities(authCodes);
      }
    });
  }, [usersServiceObject, id]);

  // Handle update response (for template-only changes)
  useEffect(() => {
    AppUtils.resolveServiceObject(updateServiceObject, REQUEST_USERS_UPDATE_ID, () => {
      setIsSaving(false);
      setConfirmModalOpen(false);
      navigate(RouteConstants.ROUTE_PERMISSIONS + '?tab=agents');
    }, () => {
      setIsSaving(false);
    });
  }, [updateServiceObject, navigate]);

  // Handle profile agent create response
  useEffect(() => {
    AppUtils.resolveServiceObject(profileAgentCreateServiceObject, REQUEST_PROFILE_AGENT_CREATE_ID, () => {
      setIsSaving(false);
      setNameModalOpen(false);
      navigate(RouteConstants.ROUTE_PERMISSIONS + '?tab=agents');
    }, () => {
      setIsSaving(false);
    });
  }, [profileAgentCreateServiceObject, navigate]);

  // Get template authorities for comparison
  const getTemplateAuthorities = useCallback((templateId: string | null): string[] => {
    if (!templateId) return [];
    const profile = profiles.find(p => p.id === parseInt(templateId));
    if (!profile) return [];
    return (profile.authorities || []).map((auth: any) => 
      typeof auth === 'string' ? auth : auth.code
    );
  }, [profiles]);

  // Check if permissions have changed from the selected template
  const hasPermissionsChanged = useMemo(() => {
    const templateAuthorities = getTemplateAuthorities(selectedTemplate);
    if (templateAuthorities.length === 0 && selectedAuthorities.length === 0) return false;
    
    const sortedTemplate = [...templateAuthorities].sort();
    const sortedSelected = [...selectedAuthorities].sort();
    
    if (sortedTemplate.length !== sortedSelected.length) return true;
    return sortedTemplate.some((auth, index) => auth !== sortedSelected[index]);
  }, [selectedTemplate, selectedAuthorities, getTemplateAuthorities]);

  const handlePermissionChange = (authorityName: string, enabled: boolean) => {
    if (enabled) {
      setSelectedAuthorities(prev => [...prev, authorityName]);
    } else {
      setSelectedAuthorities(prev => prev.filter(a => a !== authorityName));
    }
  };

  const handleTemplateChange = (value: string | null) => {
    setSelectedTemplate(value);
    if (value) {
      const selectedProfile = profiles.find(p => p.id === parseInt(value));
      if (selectedProfile) {
        const authCodes = (selectedProfile.authorities || []).map((auth: any) => 
          typeof auth === 'string' ? auth : auth.code
        );
        setSelectedAuthorities(authCodes);
      }
    }
  };

  const handleCancel = () => {
    navigate(RouteConstants.ROUTE_PERMISSIONS + '?tab=agents');
  };

  const handleSaveClick = () => {
    if (hasPermissionsChanged) {
      // Permissions changed - need to create new profile, ask for name
      setNewProfileName('');
      setNameModalOpen(true);
    } else {
      // Only template changed or nothing changed - show confirm modal
      setConfirmModalOpen(true);
    }
  };

  const handleConfirmSave = () => {
    if (!id || !selectedTemplate || !agent) return;
    setIsSaving(true);
    const userData = {
      firstName: agent.fullName.split(' ')[0] || '',
      lastName: agent.fullName.split(' ').slice(1).join(' '),
      countryCode: agent.countryCode || '',
      phoneNumber: agent.phoneNumber || '',
      status: agent.status,
      profile: { id: parseInt(selectedTemplate) },
    };
    UserService.usersUpdate(parseInt(id), userData as any);
  };

  const handleCreateProfileSave = () => {
    if (!id || !newProfileName.trim()) return;
    setIsSaving(true);
    
    const code = newProfileName.trim().toUpperCase().replace(/\s+/g, '_');
    const profileData = {
      code,
      name: newProfileName.trim(),
      authorities: selectedAuthorities,
    };
    
    ProfileService.profileAgentCreate(parseInt(id), profileData);
  };

  const isLoading = useCallback(() => {
    return (profilesServiceObject && profilesServiceObject.status === REQUEST_PENDING) ||
           (permissionsServiceObject && permissionsServiceObject.status === REQUEST_PENDING) ||
           (usersServiceObject && usersServiceObject.status === REQUEST_PENDING);
  }, [profilesServiceObject, permissionsServiceObject, usersServiceObject]);

  const templateOptions = useMemo(() =>
    profiles.map(p => ({ value: p.id.toString(), label: p.name })),
  [profiles]);

  // Calculate deviations from template
  const deviations = useMemo(() => {
    const templateAuthorities = getTemplateAuthorities(selectedTemplate);
    const added = selectedAuthorities.filter(a => !templateAuthorities.includes(a));
    const removed = templateAuthorities.filter(a => !selectedAuthorities.includes(a));
    return { added, removed };
  }, [selectedTemplate, selectedAuthorities, getTemplateAuthorities]);

  const rows = useMemo(() => {
    return availablePermissions.map((permission: AuthorityResponse, index: number) => {
      const rowBg = index % 2 === 1 ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-gray-0)';
      const permissionCode = permission.code || permission.name || '';
      const isEnabled = selectedAuthorities.includes(permissionCode);
      return (
        <Table.Tr key={permissionCode}>
          <Table.Td style={{ backgroundColor: rowBg }}>{permission.description || permissionCode}</Table.Td>
          <Table.Td style={{ width: 100, textAlign: 'right', backgroundColor: rowBg }}>
            <Switch
              checked={isEnabled}
              onChange={(event) => handlePermissionChange(permissionCode, event.currentTarget.checked)}
              styles={{ track: { cursor: 'pointer' }, thumb: { cursor: 'pointer' } }}
            />
          </Table.Td>
        </Table.Tr>
      );
    });
  }, [availablePermissions, selectedAuthorities]);

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t('permissions.agent.title'))} authority={AuthorityConstants.ROLE_PRF_CUD}>
      <Container>
        <Box pos="relative">
          <Loader overlay={true} visible={isLoading() || !agent} />
          <Tabs value="agents" variant="unstyled">
            <Tabs.List>
              <Tabs.Tab
                value="templates"
                className={displayClasses.tabList}
                onClick={() => navigate(RouteConstants.ROUTE_PERMISSIONS)}
              >
                {t('permissions.tabs.templates')}
              </Tabs.Tab>
              <Tabs.Tab
                value="agents"
                className={displayClasses.tabList}
                onClick={() => navigate(RouteConstants.ROUTE_PERMISSIONS + '?tab=agents')}
              >
                {t('permissions.tabs.agents')}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Title order={3} mt="md" mb="md">
            {agent?.fullName || ''}
          </Title>

          <Box mb="md">
            <Select
              label={t('permissions.agent.appliedTemplate')}
              data={templateOptions}
              value={selectedTemplate}
              onChange={handleTemplateChange}
              style={{ width: 200 }}
              rightSection={isSelectOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              onDropdownOpen={() => setIsSelectOpen(true)}
              onDropdownClose={() => setIsSelectOpen(false)}
            />
            
            {hasPermissionsChanged && (
              <Box mt="xs">
                <Text size="sm" c="dimmed">{t('permissions.agent.templateDeviation')}:</Text>
                {deviations.added.length > 0 && (
                  <Text size="sm" c="green">
                    + {deviations.added.map(code => {
                      const perm = availablePermissions.find(p => p.code === code);
                      return perm?.description || code;
                    }).join(', ')}
                  </Text>
                )}
                {deviations.removed.length > 0 && (
                  <Text size="sm" c="red">
                    - {deviations.removed.map(code => {
                      const perm = availablePermissions.find(p => p.code === code);
                      return perm?.description || code;
                    }).join(', ')}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          <Table className={tableClasses.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('permissions.permission')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('permissions.allowed')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={handleCancel} style={{ cursor: 'pointer' }}>
              {t('action.cancel')}
            </Button>
            <Button color="green" radius="md" size="sm" fz={14} onClick={handleSaveClick} style={{ cursor: 'pointer' }}>
              {t('action.save')}
            </Button>
          </Group>
        </Box>

        {/* Confirm modal for template-only changes */}
        <Modal
          opened={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          title={t('permissions.confirmModal.title')}
        >
          <Text mb="xl">{t('permissions.confirmModal.description')}</Text>
          <Group justify="center" gap="md">
            <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={() => setConfirmModalOpen(false)} style={{ cursor: 'pointer' }}>
              {t('action.cancel')}
            </Button>
            <Button color="green" radius="md" size="sm" fz={14} onClick={handleConfirmSave} loading={isSaving} style={{ cursor: 'pointer' }}>
              {t('permissions.confirmModal.confirm')}
            </Button>
          </Group>
        </Modal>

        {/* Name modal for creating new agent profile */}
        <Modal
          opened={nameModalOpen}
          onClose={() => setNameModalOpen(false)}
          title={t('permissions.nameModal.title')}
        >
          <TextInput
            label={t('permissions.nameModal.label')}
            placeholder={t('permissions.nameModal.placeholder')}
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.currentTarget.value)}
            mb="xl"
          />
          <Group justify="center" gap="md">
            <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={() => setNameModalOpen(false)} style={{ cursor: 'pointer' }}>
              {t('action.cancel')}
            </Button>
            <Button 
              color="green" 
              radius="md" 
              size="sm" 
              fz={14} 
              onClick={handleCreateProfileSave} 
              loading={isSaving} 
              disabled={!newProfileName.trim()}
              style={{ cursor: 'pointer' }}
            >
              {t('action.save')}
            </Button>
          </Group>
        </Modal>
      </Container>
    </LayoutStandard>
  );
};

export default AgentPermissionsPage;
